/**
 * Users — admin list (coverage §2.5, User `[P]`). Resolving every `*_user_id`
 * to a human RefLink is the highest-leverage relations fix, so the User module
 * is fully wired even though `/api/users` is `[PROPOSED]`.
 *
 * Degrade, never crash: the list drives the one shared `DataTable`, and when the
 * backing endpoint hasn't shipped (the query errors) it swaps to the calm
 * `[PROPOSED]` labeled-empty teaser instead of a red error panel.
 */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { Plus, Users as UsersIcon } from "@/components/ui/icon";
import type { User } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { StatusBadge } from "@/components/app/StatusBadge";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "../projects/_shared";
import { RowActions } from "../_shared/ListScaffold";
import { ProposedListPage } from "../_shared/ProposedModule";

export function UserList() {
  const go = useGo();
  const { editUrl, showUrl, createUrl } = useNavigation();
  const { mutate: deleteUser } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<User | null>(null);

  const columns = useMemo<ColumnDef<User, unknown>[]>(
    () => [
      { id: "display_name", accessorKey: "display_name", header: "Name" },
      { id: "email", accessorKey: "email", header: "Email" },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) =>
          new Date(row.original.created_at).toLocaleDateString(),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: showUrl("users", row.original.id) })}
            onEdit={() => go({ to: editUrl("users", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  const table = useTable<User, HttpError>({
    columns,
    refineCoreProps: {
      resource: "users",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
      queryOptions: { retry: false },
      errorNotification: false,
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  // Backend not yet shipped → calm labeled-empty teaser (never a red panel).
  if (core.tableQuery.isError) {
    return (
      <ProposedListPage
        title="Users"
        description="People and service accounts in the operating core."
        icon={UsersIcon}
        endpoint="/api/users"
        testId="users-proposed"
      >
        <Button onClick={() => go({ to: createUrl("users") })}>
          <Plus className="mr-2 h-4 w-4" /> New User
        </Button>
      </ProposedListPage>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="People and service accounts in the operating core."
        action={
          <Button onClick={() => go({ to: createUrl("users") })}>
            <Plus className="mr-2 h-4 w-4" /> New User
          </Button>
        }
      />

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder="Search users…"
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No users yet."
        onRowClick={(u) => go({ to: showUrl("users", u.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete user?"
        description={
          <>
            This permanently deletes{" "}
            <span className="font-medium">{pendingDelete?.display_name}</span>.
            This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteUser({ resource: "users", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

export default UserList;
